/* =====================================================================
   NEW CUSTOMER LANDING PAGE — behavior layer (vanilla, no framework)
   ---------------------------------------------------------------------
   Loaded `defer` in <head> ONLY on template `page.premium-tequila-delivered`
   (gated in layout/theme.liquid). Rebuilds the prototype's React
   interactions. No-ops gracefully if the page markup isn't present.

   Phase 0 = skeleton wiring for the three shared behaviors. Section
   markup is filled in during Phase 1; these handlers are event-delegated
   / observer-based so they pick up the real markup with no changes.
   ===================================================================== */
(function () {
  'use strict';

  function init() {
    var page = document.querySelector('.nc-page');
    if (!page) return; // not on the LP

    initReveal();
    initTheater();
    initVideos();
    initSmoothScroll();
    initMobileBar();
  }

  /* ---- Sticky mobile claim bar: reveal past 70% of a screen scroll ---- */
  function initMobileBar() {
    var bar = document.querySelector('[data-nc-mobilebar]');
    if (!bar) return;
    function onScroll() {
      var past = window.scrollY > window.innerHeight * 0.7;
      bar.classList.toggle('is-show', past);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---- Click-to-play videos (custom play badge → native controls) ---- */
  function initVideos() {
    document.querySelectorAll('.nc-page .nc-video').forEach((box) => {
      const v = box.querySelector('video');
      if (!v) return;

      const start = () => {
        v.setAttribute('controls', 'controls');
        // play() returns a Promise that rejects if playback is blocked (e.g. autoplay policy);
        v.play()?.catch(() => {
          console.log('video could not start');
        });
      };

      v.addEventListener('play', () => box.classList.add('is-playing'));
      v.addEventListener('pause', () => box.classList.remove('is-playing'));

      box.querySelector('.nc-video-play')?.addEventListener('click', start);

      // Initial poster click (before native controls exist)
      box.addEventListener('click', () => {
        if (!v.hasAttribute('controls')) start();
      });
    });
  }

  /* ---- 1. Scroll-reveal: add .is-in to .nc-rv when it enters view ---- */
  function initReveal() {
    var els = document.querySelectorAll('.nc-page .nc-rv');
    if (!els.length) return;

    // Reduced-motion or no IO support → just show everything (base style).
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ---- 2. Video theater: tab → active video/notes ----
     Delegated so it works once Phase 1 renders the real tabs.
     Expected markup (Phase 1):
       [data-nc-theater]
         [data-nc-tab="<id>"] ... (buttons)
         [data-nc-pane="<id>"] ... (video + notes panes)  */
  function initTheater() {
    var theater = document.querySelector('.nc-page [data-nc-theater]');
    if (!theater) return;

    function paintTabs(activeTab) {
      theater.querySelectorAll('[data-nc-tab]').forEach(function (t) {
        var on = t === activeTab;
        t.classList.toggle('is-active', on);
        t.style.borderColor = on ? (t.getAttribute('data-accent') || '') : '';
      });
    }

    // Initial paint: accent-border the default-active tab.
    var initial = theater.querySelector('[data-nc-tab].is-active') || theater.querySelector('[data-nc-tab]');
    if (initial) paintTabs(initial);

    theater.addEventListener('click', function (e) {
      var tab = e.target.closest('[data-nc-tab]');
      if (!tab || !theater.contains(tab)) return;

      var id = tab.getAttribute('data-nc-tab');
      paintTabs(tab);
      theater.querySelectorAll('[data-nc-pane]').forEach(function (p) {
        var on = p.getAttribute('data-nc-pane') === id;
        p.classList.toggle('is-active', on);
        if (!on) {
          var v = p.querySelector('video');
          if (v && !v.paused) v.pause();
        }
      });
    });
  }

  /* ---- 3. Smooth-scroll for in-page anchors (hero offer/CTA → #kit etc.) ---- */
  function initSmoothScroll() {
    document.querySelectorAll('.nc-page a[href^="#"]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        var id = a.getAttribute('href');
        if (!id || id === '#') return;
        var target = document.querySelector(id);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
